export const NAME = 'incomeVerification';
export const INCOME_CALCULATION_PATH = `${NAME}/calculateIncome`;
export const SEND_REPORT_PATH = `${NAME}/sendReport`;
export const DISPUTE_PATH = `${NAME}/dispute`;

// User-facing message for a 422 (no_processable_sources) from the FVS combined
// endpoint (ITL-2575). Shared by the thunk rejection and its tests so the two
// cannot drift.
export const CALCULATE_INCOME_UNPROCESSABLE_MESSAGE =
  'Unable to calculate income from the provided documents.';

type ErrorWarningMapping = {
  type: 'warning' | 'error';
  message: string;
};

export const ERROR_WARNING_MAPPINGS: Record<number, ErrorWarningMapping> = {
  2000: {
    type: 'warning',
    message: 'Gross pay is missing from one or more documents.'
  },
  2003: {
    type: 'warning',
    message: 'The recipient name does not match the applicant name associated with this submission.'
  },
  2004: {
    type: 'warning',
    message:
      'This calculation is based on a pay period shorter than the minimum required for this property.'
  },
  2006: {
    type: 'warning',
    message: 'The pay start and/or end dates are missing from one or more documents.'
  },
  2008: {
    type: 'warning',
    message: 'Income Source is missing from one or more documents.'
  },
  2009: {
    type: 'warning',
    message: 'One or more documents within this submission are older than 90 days.'
  },
  2010: {
    type: 'warning',
    message: 'Document has been excluded from the total income calculation amount.'
  },
  2011: {
    type: 'warning',
    message:
      'One or more of the provided income sources was a duplicate and not included in the total income amount.'
  },
  2012: {
    type: 'warning',
    message:
      'One or more documents within this submission are not consecutive to the most recent valid documents.'
  },
  2018: {
    type: 'warning',
    message:
      'One or more of the connected accounts appear to be from a joint account where the applicant may not be the primary account holder.'
  },
  2019: {
    type: 'warning',
    message:
      'One or more of the sources included in the gross income calculation may include non-recurring income.'
  },
  2020: {
    type: 'warning',
    message: 'One or more documents within this submission may be from a business account.'
  },
  2021: {
    type: 'warning',
    message:
      "One or more of the uploaded tax documents are from a prior tax year and may not reflect the applicant's current income. Please confirm that the most recent tax return has been provided."
  },
  2022: {
    type: 'warning',
    message:
      'One or more of the uploaded tax documents are missing some required information, which may impact the income calculation. Please review the documents to confirm they are complete.'
  },
  2023: {
    type: 'warning',
    message:
      "One or more of the uploaded tax documents reflect a Married Filing Jointly return. The reported income may include a spouse's earnings, which could overstate the applicant's individual income."
  },
  3001: {
    type: 'error',
    message: 'Gross pay is missing from all documents.'
  },
  3002: {
    type: 'error',
    message: 'Recipient Name is missing from one or more documents.'
  },
  3003: {
    type: 'error',
    message: 'The recipient name does not match the applicant name associated with this submission.'
  },
  3004: {
    type: 'error',
    message:
      'Documents do not span the minimum number of consecutive days required for this property.'
  },
  3005: {
    type: 'error',
    message:
      'The documents within this submission do not represent pay issued within the past 90 days.'
  },
  3007: {
    type: 'error',
    message: 'The pay start and/or end dates are missing from all documents.'
  },
  3008: {
    type: 'error',
    message: 'One or more documents within this submission are older than 90 days.'
  },
  3019: {
    type: 'error',
    message:
      'One or more of the sources included in the gross income calculation may include non-recurring income.'
  },
  3020: {
    type: 'error',
    message: 'One or more documents within this submission may be from a business account.'
  },
  3021: {
    type: 'error',
    message:
      "We cannot verify the applicant's income because the uploaded tax documents are from a prior tax year and do not reflect the applicant's current income. Please request the most recent tax return."
  },
  3022: {
    type: 'error',
    message:
      "We cannot verify the applicant's income because none of the uploaded tax documents contained the information needed to calculate income. Please request complete tax return documents."
  },
  3023: {
    type: 'error',
    message:
      "We cannot verify the applicant's income because the uploaded tax documents reflect a Married Filing Jointly return. Income cannot be attributed to the applicant individually. Please request a separate income document, such as W-2s or pay stubs."
  },
  4001: {
    type: 'warning',
    message:
      "The gross income calculation was updated using data from the applicant's uploaded documents or connected accounts."
  },
  4002: {
    type: 'warning',
    message:
      "The gross income calculation was updated using data from the applicant's uploaded documents or connected accounts."
  },
  4003: {
    type: 'warning',
    message:
      "The gross income calculation was updated using data from the applicant's uploaded documents or connected accounts."
  },
  4004: {
    type: 'warning',
    message:
      "The gross income calculation was updated using data from the applicant's uploaded documents or connected accounts."
  },
  4005: {
    type: 'warning',
    message: 'Name mismatch was overridden for this document.'
  }
};

export const MISMATCH_CODES = [2003, 3003];
export const OVERRIDE_APPLIED_CODE = 4005;

export type CalculationDocument = {
  document_id: string;
  document_errors?: number[];
  reason_codes?: { code: number }[];
  applicant_name?: string;
  recipient_name?: string;
  document_start_date?: string;
  document_end_date?: string;
  gross_income?: number | string;
  income_source?: string;
  source_type?: string;
  document_source_type?: string;
  document_type?: string;
  has_non_recurring_income?: boolean;
  base_pay_income_amount?: number | string | null;
};

export const eligibleDocuments = [
  'PAYSTUB',
  'DEPARTMENT_OF_VETERANS_AFFAIRS_BENEFIT_LETTER',
  'SOCIAL_SECURITY_BENEFITS_LETTER',
  'SOCIAL_SECURITY_STATEMENT'
];

export const defaultStateIVFields = {
  payPeriodStartDate: '',
  payPeriodEndDate: '',
  payPeriodDateRange: '',
  recipientName: '',
  grossPay: '',
  incomeSource: '',
  hasNonRecurringIncome: false,
  basePayIncomeAmount: ''
};
